import React from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, Lock, Shield } from "lucide-react";
import { RevealOnScroll } from "../shared";
import {
  MARGIN_THESIS,
  MARGIN_SCAN,
  MARGIN_ZONES,
  MARGIN_POINTS,
} from "./marginProtectionContent";

const zoneStyles = {
  visible: {
    shell: "border-partner bg-[#11151F]",
    label: "text-partner-muted",
    title: "text-partner",
    item: "text-partner-muted",
    icon: Eye,
  },
  protected: {
    shell: "border-[#00D2FF]/45 bg-[#00D2FF]/8",
    label: "text-partner-accent",
    title: "text-partner",
    item: "text-partner",
    icon: Shield,
  },
  redacted: {
    shell: "border-partner bg-[#080A0F]",
    label: "text-partner-muted/70",
    title: "text-partner-muted blur-[2px] select-none",
    item: "text-partner-muted/50 blur-[1.5px] select-none",
    icon: EyeOff,
  },
};

const VaultZone = ({ zone, index }) => {
  const styles = zoneStyles[zone.tone];
  const Icon = styles.icon;
  const isRedacted = zone.tone === "redacted";
  const isProtected = zone.tone === "protected";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className={`relative rounded-xl border p-4 sm:p-5 flex flex-col min-h-[10rem] ${styles.shell}`}
      data-testid={`vault-zone-${zone.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className={`text-xs font-semibold tracking-wide ${styles.label}`}>{zone.label}</span>
        <Icon className={`w-4 h-4 flex-shrink-0 ${isProtected ? "text-partner-accent" : "text-partner-muted"}`} />
      </div>

      <p className={`text-sm font-bold mb-3 leading-snug ${styles.title}`}>
        {isRedacted ? "████████ ████████" : zone.title}
      </p>

      <ul className="space-y-1.5 mt-auto">
        {zone.items.map((item) => (
          <li key={item} className={`text-xs font-body leading-snug ${styles.item}`}>
            {isRedacted ? "████████████" : item}
          </li>
        ))}
      </ul>

      {isRedacted && (
        <div className="absolute inset-0 rounded-xl flex items-center justify-center pointer-events-none">
          <span className="flex items-center gap-1.5 text-xs text-partner-muted">
            <Lock className="w-3 h-3" />
            REDACTED
          </span>
        </div>
      )}
    </motion.div>
  );
};

const MarginProtectionSection = () => (
  <section className="section-padding section-alt relative overflow-hidden" data-testid="margin-protection">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <RevealOnScroll>
        <h2 className="text-3xl sm:text-4xl font-bold text-partner leading-tight mb-3 max-w-3xl">
          {MARGIN_THESIS.headline}{" "}
          <span className="gradient-text">{MARGIN_THESIS.headlineAccent}</span>
        </h2>
        <p className="text-partner-muted font-body max-w-2xl leading-relaxed mb-3">
          {MARGIN_THESIS.insight}
        </p>
        <p className="text-sm text-partner-accent font-body mb-8" data-testid="margin-scan-strip">
          {MARGIN_SCAN.map((s) => s.value).join(" · ")}{" "}
          <span className="text-partner-muted">
            · {MARGIN_SCAN.map((s) => s.label.toLowerCase()).join(", ")}
          </span>
        </p>
      </RevealOnScroll>

      <RevealOnScroll delay={0.08}>
        <div className="grid md:grid-cols-3 gap-4 mb-10" data-testid="isolation-vault">
          {MARGIN_ZONES.map((zone, index) => (
            <VaultZone key={zone.id} zone={zone} index={index} />
          ))}
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.12}>
        <div className="space-y-6 max-w-3xl" data-testid="protection-layers">
          {MARGIN_POINTS.map((point) => (
            <div key={point.layer} data-testid={`margin-layer-${point.layer}`}>
              <h3 className="text-base font-bold text-partner mb-2">{point.title}</h3>
              <p className="text-sm text-partner-muted font-body leading-relaxed">{point.description}</p>
            </div>
          ))}
        </div>
      </RevealOnScroll>
    </div>
  </section>
);

export default MarginProtectionSection;
